/**
 * agent-channel-contracts.ts
 *
 * Domain contracts for Enterprise Agentic Workspaces:
 * - Granular RBAC channel access & tool permissions
 * - Canonical URI-namespaced RAG collection definitions & grounding policies
 * - ActionLifecycle state machine, canonical payload hashing, and ActionTokenBinding
 * - ChannelSession isolation and multi-turn message history
 * - Fine-grained RuntimeTrace & phased SSE telemetry events
 */

import { sha256Sync } from '../../core/genesis-provisioner.js';

// ── Canonical JSON Serialization Helper ──────────────────────────────────────

export function canonicalJson(obj: any): string {
  if (obj === null || typeof obj !== 'object') {
    return JSON.stringify(obj);
  }
  if (Array.isArray(obj)) {
    return '[' + obj.map((item) => canonicalJson(item)).join(',') + ']';
  }
  const keys = Object.keys(obj).sort();
  const keyValues = keys.map((key) => `${JSON.stringify(key)}:${canonicalJson(obj[key])}`);
  return '{' + keyValues.join(',') + '}';
}

export function computePayloadHash(payload: Record<string, unknown>): string {
  const canonical = canonicalJson(payload);
  return sha256Sync(canonical);
}

// ── Access Control & RBAC Types ──────────────────────────────────────────────

export type ChannelAccessRole = 'owner' | 'admin' | 'developer' | 'sales' | 'support' | 'member' | 'guest' | string;
export type PublicAccessLevel = 'authenticated' | 'anonymous' | 'disabled';

export interface ChannelAccessPolicy {
  readonly publicAccess: PublicAccessLevel;
  readonly allowedRoles: readonly ChannelAccessRole[];
  readonly allowedUserGroupIds: readonly string[];
  readonly allowedPrincipalIds: readonly string[];
  readonly deniedPrincipalIds: readonly string[];
}

export interface ActionPermission {
  readonly toolName: string;
  readonly allowedRoles: readonly ChannelAccessRole[];
  readonly allowedGroupIds: readonly string[];
  readonly allowedPrincipalIds: readonly string[];
  readonly deniedPrincipalIds: readonly string[];
}

export type AccessVerdictRule =
  | 'explicit_deny'
  | 'explicit_allow'
  | 'role_match'
  | 'group_match'
  | 'public_channel'
  | 'default_deny';

export interface ChannelAccessVerdict {
  readonly allowed: boolean;
  readonly channelId: string;
  readonly principalId: string;
  readonly matchedRule: AccessVerdictRule;
  readonly reason?: string;
  readonly evaluatedAt: string;
}

// ── RAG Knowledge & Grounding Types ──────────────────────────────────────────

export type RagScopeType = 'global' | 'plugin' | 'channel';

export interface RagCollectionDefinition {
  readonly id: string; // e.g. "plugin:commerce:catalog", "global:public-faq"
  readonly name: string;
  readonly scopeType: RagScopeType;
  readonly scopeId?: string; // owning pluginId or channelId
  readonly description?: string;
  readonly documentCount: number;
  readonly chunkCount: number;
  readonly lastIndexedAt: string;
}

export interface RagCollectionBinding {
  readonly collectionId: string;
  readonly ownerType: RagScopeType;
  readonly ownerId: string;
  readonly allowedPrincipalTypes: readonly string[];
  readonly allowedChannelIds: readonly string[];
  readonly enabled: boolean;
}

export interface GroundingPolicy {
  readonly minimumSimilarity: number;
  readonly minimumEvidenceChunks: number;
  readonly requireCitationForClaims: boolean;
  readonly allowUngroundedConversation: boolean;
}

// ── Channel Definitions ──────────────────────────────────────────────────────

export type StandardAgentChannelId =
  | 'shopping'
  | 'scrum'
  | 'wiki'
  | 'crm'
  | 'cms'
  | 'advisor'
  | string;

export interface AgentChannelDefinition {
  readonly id: StandardAgentChannelId;
  readonly name: string;
  readonly description: string;
  readonly icon: string;
  readonly modelKind: 'llm';
  readonly llmModelId: string;
  readonly needleModelId?: string;
  readonly pluginId?: string;
  readonly ragCollectionIds: readonly string[];
  readonly groundingPolicy: GroundingPolicy;
  readonly tools: readonly string[];
  readonly actionPermissions?: readonly ActionPermission[];
  readonly accessPolicy: ChannelAccessPolicy;
  readonly enabled: boolean;
  readonly isScaffolded?: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

// ── Action State Machine & Token Security ────────────────────────────────────

export type ActionLifecycleState =
  | 'proposed'
  | 'executing'
  | 'executed'
  | 'failed'
  | 'cancelled'
  | 'expired';

export const ACTION_TRANSITIONS: Record<ActionLifecycleState, readonly ActionLifecycleState[]> = {
  proposed: ['executing', 'cancelled', 'expired'],
  executing: ['executed', 'failed'],
  failed: ['executing', 'cancelled'],
  executed: [],
  cancelled: [],
  expired: [],
};

export interface ProposedAction {
  readonly actionId: string;
  readonly principalId: string;
  readonly sessionId: string;
  readonly channelId: string;
  readonly toolName: string;
  readonly payload: Record<string, unknown>;
  readonly payloadHash: string;
  readonly lifecycleState: ActionLifecycleState;
  readonly tokenBindingId: string;
  readonly idempotencyKey: string;
  readonly createdAt: string;
  readonly expiresAt: string;
  readonly revision: number;
  readonly error?: string;
}

export interface ActionTokenBinding {
  readonly actionId: string;
  readonly principalId: string;
  readonly sessionId: string;
  readonly channelId: string;
  readonly payloadHash: string;
  readonly tokenHash: string;
  readonly expiresAt: string;
  readonly consumedAt?: string;
}

export interface ActionExecutionResult {
  readonly actionId: string;
  readonly success: boolean;
  readonly executedAt: string;
  readonly resultData?: Record<string, unknown>;
  readonly error?: string;
}

export interface ActionReproposalRequest {
  readonly originalActionId: string;
  readonly modifiedPayload: Record<string, unknown>;
  readonly clientReason?: string;
}

// ── Multi-Turn Session & Messages ────────────────────────────────────────────

export interface ChannelMessage {
  readonly id: string;
  readonly role: 'user' | 'assistant' | 'system';
  readonly content: string;
  readonly timestamp: string;
  readonly citations?: readonly string[];
  readonly actionId?: string;
  readonly extractionPreview?: Record<string, unknown>;
}

export interface ChannelSession {
  readonly sessionId: string;
  readonly channelId: string;
  readonly principalId: string;
  readonly messages: readonly ChannelMessage[];
  readonly activeActionIds: readonly string[];
  readonly rollingSummary?: string;
  readonly lastActiveAt: string;
}

// ── Tool Execution Wrapper & Timeouts ────────────────────────────────────────

export interface ToolExecutionContext {
  readonly signal: AbortSignal;
  readonly principalId: string;
  readonly channelId: string;
  readonly requestId: string;
  readonly idempotencyKey: string;
}

export interface ToolResult<T = unknown> {
  readonly tool: string;
  readonly ok: boolean;
  readonly data?: T;
  readonly error?: 'TIMEOUT' | 'ABORTED' | 'UNAUTHORIZED' | 'VALIDATION' | 'UNAVAILABLE';
  readonly fallback?: unknown[];
  readonly elapsedMs: number;
}

// ── SSE Phased Telemetry & Trace ─────────────────────────────────────────────

export type GatewayPhase =
  | 'authorizing'
  | 'loading_context'
  | 'extracting_and_retrieving'
  | 'executing_tools'
  | 'generating'
  | 'action_proposed'
  | 'completed'
  | 'failed'
  | 'aborted';

export interface AgentStreamEvent {
  readonly eventId: string;
  readonly requestId: string;
  readonly phase: GatewayPhase;
  readonly payload: Record<string, unknown>;
  readonly sequence: number;
  readonly occurredAt: string;
}

export interface RuntimeTrace {
  readonly requestId: string;
  readonly correlationId: string;
  readonly channelId: string;
  readonly principalId: string;
  readonly totalTurnLatencyMs: number;

  // Extraction Tier (Needle 2)
  readonly needleNodeId?: string;
  readonly needleRuntimeId?: string;
  readonly needleInstanceIndex?: number;
  readonly extractionLatencyMs?: number;
  readonly schemaValidated: boolean;
  readonly extractionRepairCount?: number;

  // Grounding Tier (RAG)
  readonly ragRetrievalLatencyMs?: number;
  readonly queriedCollectionIds: readonly string[];
  readonly retrievedChunkCount: number;
  readonly verifiedCitations: readonly string[];
  readonly citationCoverage: number;
  readonly insufficientEvidence: boolean;

  // Reasoning Tier (LLM)
  readonly llmNodeId?: string;
  readonly llmRuntimeId?: string;
  readonly llmLatencyMs?: number;
  readonly promptTokensEstimate: number;
  readonly completionTokensEstimate: number;

  // Action Tier
  readonly proposedActionCount: number;
  readonly executedActionCount: number;

  // Status
  readonly terminalStatus: 'completed' | 'failed' | 'aborted';
  readonly error?: string;
}

// ── Custom Errors ────────────────────────────────────────────────────────────

export class ChannelAccessDeniedError extends Error {
  constructor(public readonly verdict: ChannelAccessVerdict) {
    super(`Access denied for channel ${verdict.channelId}: ${verdict.reason || verdict.matchedRule}`);
    this.name = 'ChannelAccessDeniedError';
  }
}

export class ActionExpiredError extends Error {
  constructor(public readonly actionId: string) {
    super(`Action ${actionId} has expired`);
    this.name = 'ActionExpiredError';
  }
}

export class ActionConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ActionConflictError';
  }
}

export class PayloadHashMismatchError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PayloadHashMismatchError';
  }
}

export class ActionUnauthorizedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ActionUnauthorizedError';
  }
}

export class ClientAbortError extends Error {
  constructor(message = 'Client disconnected') {
    super(message);
    this.name = 'ClientAbortError';
  }
}
